/*
  tmc-gut-pdp.js — the behaviour the Gut PDP sections own themselves: the 390 pager dots under the
  organism tiles (board 29FU-0, NW-37), and two lines that follow the chosen tier — the lab band's method
  (NW-37c) and the steps band's turnaround (NW-38).

  Everything else on these sections is either CSS or already owned by assets/tmc-product.js (panel
  selection, gallery stage, the hand-off to the frozen patient-details form) and is not duplicated here.

  Progressive: the tile row is a scroll container in CSS, so it swipes whether or not this file runs.
  All this does is mirror which tile the row has reached onto the dots. It binds on shopify:section:load
  as well as DOMContentLoaded so the theme editor gets the behaviour after a re-render.
*/
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.pdpOrgBound === '1') return;
    var track = root.querySelector('[data-tmc-pdp-track]');
    var pager = root.querySelector('[data-tmc-pdp-pager]');
    if (!track || !pager) return;

    var dots = Array.prototype.slice.call(pager.children);
    var tiles = Array.prototype.slice.call(track.children);
    if (dots.length === 0 || tiles.length === 0) return;
    root.dataset.pdpOrgBound = '1';

    function mark(index) {
      dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === index); });
    }

    /* The tile nearest the left edge of the scroll port is the one being read. offsetLeft and
       scrollLeft are read rather than getBoundingClientRect: both are cheap and neither forces a
       layout flush mid-scroll, which matters on a touch drag. Both are relative to the same
       offsetParent, so the arithmetic holds inside the theme editor's frame too. */
    function nearest() {
      var origin = track.offsetLeft + track.scrollLeft;
      var nearestIndex = 0;
      var smallestGap = Infinity;
      tiles.forEach(function (tile, i) {
        var gap = Math.abs(tile.offsetLeft - origin);
        if (gap < smallestGap) { smallestGap = gap; nearestIndex = i; }
      });
      return nearestIndex;
    }

    /* Updated straight from the scroll event, with no requestAnimationFrame coalescing. An earlier
       version guarded with a "frame already queued" boolean, which strands: requestAnimationFrame
       does not fire while the document is hidden, so one scroll in a backgrounded tab left the flag
       true and the pager never updated again for the life of the page. Five integer reads per event
       is cheaper than the bug. */
    track.addEventListener('scroll', function () {
      mark(nearest());
    }, { passive: true });

    mark(nearest());
  }

  /* The lab band's method line follows the checked tier. The band is server-rendered for the page's own
     product, but the five tiers share one page and a tier change is a client-side swap (tmc-product.js
     replaces the URL and never re-renders a section), so without this the line would keep describing
     the tier the visitor arrived on. Each tier's radio carries its own data-lab-method, empty when the
     product has none; an empty value removes the line rather than leaving an empty paragraph. The text is
     set with textContent, never as markup. */
  function initLab(root) {
    if (root.dataset.pdpLabBound === '1') return;
    var head = root.querySelector('[data-tmc-pdp-lab-head]');
    if (!head) return;
    root.dataset.pdpLabBound = '1';

    function show(text) {
      var line = head.querySelector('[data-tmc-pdp-method]');
      if (!text) { if (line) line.parentNode.removeChild(line); return; }
      if (!line) {
        line = document.createElement('p');
        line.className = 'tmc-pdp-lab__method';
        line.setAttribute('data-tmc-pdp-method', '');
        head.appendChild(line);
      }
      line.textContent = text;
    }

    function onChange(event) {
      /* A theme-editor re-render replaces the band; the replacement binds its own listener. */
      if (!root.isConnected) { document.removeEventListener('change', onChange); return; }
      var input = event.target;
      if (input && input.matches && input.matches('input[data-tmc-panel]') && input.checked) {
        show(input.getAttribute('data-lab-method'));
      }
    }
    document.addEventListener('change', onChange);

    /* Agree with the hero on load: it can start on a tier other than the page's own product. */
    var checked = document.querySelector('input[data-tmc-panel]:checked');
    if (checked) show(checked.getAttribute('data-lab-method'));
  }

  /* The steps band's results kicker follows the checked tier's turnaround (NW-38), for the same reason the
     lab band's method line does: one page, five tiers, no re-render on a tier change. The server renders
     the page's own product's custom.turnaround; this just swaps the text. An empty value leaves it alone
     rather than printing a bare "STEP 03 ·". */
  function initSteps(root) {
    if (root.dataset.gutStepsBound === '1') return;
    var slot = root.querySelector('[data-tmc-gut-turnaround]');
    if (!slot) return;
    root.dataset.gutStepsBound = '1';

    function show(text) { if (text) slot.textContent = text.toUpperCase(); }

    function onChange(event) {
      if (!root.isConnected) { document.removeEventListener('change', onChange); return; }
      var input = event.target;
      if (input && input.matches && input.matches('input[data-tmc-panel]') && input.checked) {
        show(input.getAttribute('data-turnaround'));
      }
    }
    document.addEventListener('change', onChange);

    var checked = document.querySelector('input[data-tmc-panel]:checked');
    if (checked) show(checked.getAttribute('data-turnaround'));
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-tmc-pdp-org]').forEach(init);
    (scope || document).querySelectorAll('[data-tmc-pdp-lab]').forEach(initLab);
    (scope || document).querySelectorAll('[data-tmc-gut-steps]').forEach(initSteps);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
